import { useCallback, useContext, useMemo } from "react";
import { SpaceContext } from "@/contexts/space-context";
import { SettingsScopeContext } from "@/contexts/settings-scope-context";
import { LEGACY_TENANT, defaultBiz, tenantHasBiz, type BizKey, type TenantKey } from "@/domain/space";
import type { SettingsAt } from "@/domain/tenantPath";
import { menuPath } from "@/domain/menus";

/**
 * 법인 주소 만들기 — 화면 안의 고정 링크("/settings#users" 등)를 지금 법인 주소로.
 * SpaceProvider 밖(로그인 화면 등)에서도 죽지 않게 useContext 를 직접 읽고, 없으면 옛 주소 법인(중앙).
 *
 *   const tp = useTenantPath();
 *   navigate(`${tp("settings")}#users`);          // 지금 법인
 *   <Link to={tp("dispatch-view", "joongang")} /> // 데이터가 중앙 것인 내 업무 카드 등은 법인 명시
 */
export function useTenantPath(): (key: string, tenant?: TenantKey) => string {
  const ctx = useContext(SpaceContext);
  const dataTenant = ctx?.dataTenant ?? LEGACY_TENANT;
  return useCallback((key: string, tenant?: TenantKey) => menuPath(key, tenant ?? dataTenant), [dataTenant]);
}

/**
 * 데이터 조회 기준 법인 — 법인 공간이면 그 법인, 내 업무·공간 밖이면 옛 기본(중앙).
 * 모듈 캐시 키에 넣어 법인 전환 시 다른 법인 집계가 섞이지 않게 한다.
 * 2단계(tenants/{법인}/… 경로 이전)에서 repository 호출 인자로도 쓴다.
 */
export function useDataTenant(): TenantKey {
  const ctx = useContext(SpaceContext);
  return ctx?.dataTenant ?? LEGACY_TENANT;
}

/** 지금 (법인, 사업) — 사업 메뉴 주소면 그 사업, 법인 공통 메뉴·내 업무면 그 법인의 기본 사업. */
export interface CurrentTenantBiz {
  tenant: TenantKey;
  biz: BizKey;
}
export function useTenantBiz(): CurrentTenantBiz {
  const ctx = useContext(SpaceContext);
  const tenant = ctx?.dataTenant ?? LEGACY_TENANT;
  const raw = ctx?.biz ?? null;
  const biz = tenantHasBiz(tenant, raw) ? raw : defaultBiz(tenant);
  return useMemo(() => ({ tenant, biz }), [tenant, biz]);
}

/**
 * 설정 저장 위치(12 §9-3) — 설정 repository 에 그대로 넘긴다.
 *   1) 법인 환경설정 허브 안 = 사업부 칩으로 고른 (법인, 사업부)(SettingsScopeContext)
 *   2) 그 밖 = 지금 공간의 법인 · 사업(useTenantBiz). 공간 밖(로그인 화면 등)은 중앙 레미콘(옛 동작)
 * 중앙 레미콘은 tenantPath 의 JOONGANG_SETTINGS_AT_ROOT 덕분에 지금처럼 루트(system_settings)를 읽고 쓴다.
 */
export function useSettingsAt(): SettingsAt {
  const override = useContext(SettingsScopeContext);
  const cur = useTenantBiz();
  const tenant = override?.tenant ?? cur.tenant;
  const biz = override ? (override.biz ?? null) : cur.biz;
  return useMemo(() => ({ tenant, biz }), [tenant, biz]);
}
